export interface InstallState {
  /** The browser offered a prompt and it has not been used yet. */
  available: boolean;
  /** Already running as an installed app. */
  installed: boolean;
  /** Safari never fires the event; the user adds it from the share sheet. */
  manualOnly: boolean;
}

interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: InstallPromptEvent | null = null;
const listeners = new Set<(state: InstallState) => void>();

export function readInstallState(): InstallState {
  if (typeof window === 'undefined') {
    return { available: false, installed: false, manualOnly: false };
  }

  const installed =
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (window.navigator as { standalone?: boolean }).standalone === true;
  const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  return { available: deferred !== null, installed, manualOnly: iOS && !installed };
}

function publish(): void {
  const state = readInstallState();
  for (const listener of listeners) listener(state);
}

export function watchInstallPrompt(): void {
  if (typeof window === 'undefined') return;

  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferred = event as InstallPromptEvent;
    publish();
  });

  window.addEventListener('appinstalled', () => {
    deferred = null;
    publish();
  });
}

export function onInstallStateChange(listener: (state: InstallState) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  if (!deferred) return 'unavailable';

  const prompt = deferred;
  deferred = null;
  publish();

  await prompt.prompt();
  const { outcome } = await prompt.userChoice;
  return outcome;
}
